import { Text, type TextProps, Title } from "@clipboard-health/ui-components";
import { type TextVariant } from "@clipboard-health/ui-theme";
import { Box, Link, type TypographyProps } from "@mui/material";
import { fromPairs } from "lodash";
// eslint-disable-next-line no-restricted-imports
import Markdown, { type MarkdownToJSX } from "markdown-to-jsx";
import { Fragment, type ReactElement } from "react";

type MuiMarkdownProps = {
  children: string;
  variant?: TextVariant;
  color?: TextProps["color"];
  lineHeight?: TypographyProps["lineHeight"]; // TextProps doesn't expose `lineHeight` but it works
  semibold?: TextProps["semibold"];
  listMarginTop?: number;
} & Pick<MarkdownToJSX.Options, "forceBlock">;

const HEADING_COMPONENTS = ["h1", "h2", "h3", "h4", "h5", "h6"];
const HEADING_OVERRIDES: Record<string, MarkdownToJSX.Override> = fromPairs(
  HEADING_COMPONENTS.map((component) => {
    return [
      component,
      {
        component: Title,
        props: {
          gutterBottom: true,
          component,
        },
      },
    ];
  })
);

// Todo: move to CBH core ui components
export function MuiMarkdown(props: MuiMarkdownProps): ReactElement {
  const { children, variant, color, lineHeight, forceBlock, semibold, listMarginTop } = props;

  return (
    <Box
      sx={{
        "& p + ul": {
          marginTop: 0,
        },
        "& p:has(+ ul)": {
          marginBottom: 0,
        },
        "& p + ol": {
          marginTop: 0,
        },
        "& p:has(+ ol)": {
          marginBottom: 0,
        },
        "& > :last-child": {
          marginBottom: 0,
        },
      }}
    >
      <Markdown
        options={{
          wrapper: Fragment,
          disableParsingRawHTML: true,
          forceBlock,
          overrides: {
            ...HEADING_OVERRIDES,
            p: {
              component: Text,
              props: {
                paragraph: true,
                variant: variant ?? "body1",
                color: color ?? "inherit",
                lineHeight,
                semibold,
              },
            },
            a: {
              component: Link,
              props: {
                rel: "noopener noreferrer",
                target: "_blank",
              },
            },
            strong: {
              component: Text,
              props: {
                paragraph: false,
                variant: variant ?? "body1",
                color: color ?? "inherit",
                lineHeight,
                sx: { fontWeight: 600, display: "inline" },
              },
            },
            ul: {
              component: ({ children }) => (
                <ul
                  style={{
                    listStyleType: "square",
                    paddingLeft: "1.5rem",
                    marginTop: listMarginTop,
                  }}
                >
                  {children}
                </ul>
              ),
            },
            ol: {
              component: ({ children }) => (
                <ol
                  style={{
                    listStyleType: "square",
                    paddingLeft: "1.5rem",
                    marginTop: listMarginTop,
                  }}
                >
                  {children}
                </ol>
              ),
            },
            li: {
              component: ({ children }) => (
                // Need to wrap in a li element to get the list to render correctly with bullets
                <li>
                  <Text
                    variant={variant ?? "body1"}
                    color={color ?? "inherit"}
                    semibold={semibold}
                    {...(lineHeight && { lineHeight })}
                  >
                    {children}
                  </Text>
                </li>
              ),
            },
          },
        }}
      >
        {children}
      </Markdown>
    </Box>
  );
}
